import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { revertAll } from '../actions';

export interface UserDetails {
    Id?: string | number | null;
    Name?: string;
    FirstName?: string;
    LastName?: string;
    Email?: string | null;
    Username?: string;
    IsPrimary?: boolean;
    phoneNumbers?: Array<{ IsPrimary?: boolean; IsVerified?: boolean; [key: string]: unknown }>;
    [key: string]: unknown;
}

export interface IUserState {
    cognitoUser: unknown;
    authenticationDetails: unknown;
    number: unknown;
    accessToken: string;
    idToken: string;
    refreshToken: string | null;
    userDetails: UserDetails | null;
    isLoggedIn: boolean;
    selectedLang: string;
    userId: string | null;
    userName: string;
    userImageLink: string;
    userNotificationsCount: number;
}

const initialState: IUserState = {
    cognitoUser: null,
    authenticationDetails: null,
    number: null,
    accessToken: '',
    idToken: '',
    refreshToken: '',
    userDetails: null,
    isLoggedIn: false,
    selectedLang: 'en',
    userId: null,
    userName: '',
    userImageLink: '',
    userNotificationsCount: 0,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        updateLoginStatus: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                isLoggedIn: action.payload,
            };
        },
        updateLoginUserDetails: (
            state,
            action: PayloadAction<{ user: UserDetails | null; token: string; refreshToken: string | null }>,
        ) => {
            return {
                ...state,
                userDetails: action.payload.user,
                accessToken: action.payload.token,
                refreshToken: action.payload.refreshToken,
            };
        },
        setLoginUserProfileImageUrl: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                userImageLink: action.payload,
            };
        },
        setUserNotificationsCount: (state, action: PayloadAction<number>) => {
            return {
                ...state,
                userNotificationsCount: action.payload,
            };
        },
        updateNotificationPreferences: (
            state,
            action: PayloadAction<{ emailNotification?: boolean; smsNotification?: boolean }>,
        ) => {
            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    ...(action.payload.emailNotification !== undefined && {
                        EmailNotification: action.payload.emailNotification,
                    }),
                    ...(action.payload.smsNotification !== undefined && {
                        SmsNotification: action.payload.smsNotification,
                    }),
                },
            };
        },
        updateUserProfileDetails: (state, action: PayloadAction<Record<string, unknown>>) => {
            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    ...action.payload,
                },
            };
        },
    },
    extraReducers: builder => builder.addCase(revertAll, () => initialState),
});

export const {
    updateLoginStatus,
    updateLoginUserDetails,
    setLoginUserProfileImageUrl,
    setUserNotificationsCount,
    updateNotificationPreferences,
    updateUserProfileDetails,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
